export type CefrLevel = 'B1' | 'B2' | 'C1' | 'C2'

export const IELTS_CEFR_SOURCE_URL =
  'https://ielts.org/news-and-insights/finding-the-right-english-proficiency-test-for-you'

export const COUNCIL_OF_EUROPE_CEFR_SOURCE_URL =
  'https://www.coe.int/en/web/common-european-framework-reference-languages/table-1-cefr-3.3-common-reference-levels-global-scale'

interface MappedCefrComparison {
  status: 'mapped'
  band: number
  level: CefrLevel
  officialBandRange: string
  studentSummary: string
}

interface BelowStandardCefrComparison {
  status: 'below-standard-range'
  band: number
  level: null
  officialBandRange: null
  studentSummary: string
}

interface UnavailableCefrComparison {
  status: 'unavailable'
  band: null
  level: null
  officialBandRange: null
  studentSummary: string
}

export type CefrComparison =
  | MappedCefrComparison
  | BelowStandardCefrComparison
  | UnavailableCefrComparison

export interface CefrBandRange {
  min: number
  max: number
  level: CefrLevel
  label: string
  studentSummary: string
}

/**
 * IELTS-published comparison bands, with concise student-facing CEFR summaries.
 *
 * The ranges come from IELTS. The summaries are Edvantage paraphrases of the
 * Council of Europe's CEFR global scale; they are not quotations, official
 * level awards, or a fresh language assessment.
 */
export const IELTS_CEFR_BAND_RANGES: readonly CefrBandRange[] = [
  {
    min: 4,
    max: 5,
    level: 'B1',
    label: '4.0–5.0',
    studentSummary:
      'You can usually follow the main points of clear English on familiar topics and handle common study, work and travel situations.',
  },
  {
    min: 5.5,
    max: 6.5,
    level: 'B2',
    label: '5.5–6.5',
    studentSummary:
      'You can usually follow the main ideas in more complex English and communicate clearly and in useful detail.',
  },
  {
    min: 7,
    max: 8,
    level: 'C1',
    label: '7.0–8.0',
    studentSummary:
      'You can usually understand demanding English and express ideas fluently and flexibly for study, work and social settings.',
  },
  {
    min: 8.5,
    max: 9,
    level: 'C2',
    label: '8.5–9.0',
    studentSummary:
      'You can usually understand almost everything you hear or read and express fine shades of meaning precisely and fluently.',
  },
] as const

const BELOW_STANDARD_DESCRIPTOR =
  'The IELTS comparison table does not assign standard IELTS bands below 4.0 to CEFR A1 or A2, so Edvantage does not assign a CEFR level here.'

const UNAVAILABLE_DESCRIPTOR =
  'A CEFR comparison needs a valid IELTS band from 0.0 to 9.0, reported in a whole or half band.'

/**
 * A value IELTS would actually report: 0.0–9.0 in whole or half bands.
 *
 * Exported because every score-led surface needs the same gate before it says
 * anything about a band, and a second copy of this check is a second place for
 * quarter bands to leak through.
 */
export function isReportableIeltsBand(value: unknown): value is number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 9) return false
  return Math.abs(value * 2 - Math.round(value * 2)) < Number.EPSILON * 10
}

/**
 * Compare a valid IELTS whole/half band with the official IELTS CEFR guide.
 *
 * Values below 4.0 intentionally return a no-equivalent state. IELTS' public
 * standard-test table leaves A1 and A2 blank; manufacturing those labels would
 * turn a helpful comparison into a false proficiency claim.
 */
export function compareIeltsBandToCefr(value: unknown): CefrComparison {
  if (!isReportableIeltsBand(value)) {
    return {
      status: 'unavailable',
      band: null,
      level: null,
      officialBandRange: null,
      studentSummary: UNAVAILABLE_DESCRIPTOR,
    }
  }

  if (value < 4) {
    return {
      status: 'below-standard-range',
      band: value,
      level: null,
      officialBandRange: null,
      studentSummary: BELOW_STANDARD_DESCRIPTOR,
    }
  }

  const range = IELTS_CEFR_BAND_RANGES.find(({ min, max }) => value >= min && value <= max)
  if (!range) {
    // This is unreachable for valid half-band values, but keeps the function
    // fail-closed if IELTS ever changes its scale or the ranges above drift.
    return {
      status: 'unavailable',
      band: null,
      level: null,
      officialBandRange: null,
      studentSummary: UNAVAILABLE_DESCRIPTOR,
    }
  }

  return {
    status: 'mapped',
    band: value,
    level: range.level,
    officialBandRange: range.label,
    studentSummary: range.studentSummary,
  }
}
